export const BOOKING_PAYMENT_POLICY_VERSION = "booking_payment_policy_v1";

const POLICY_TYPES = new Set(["none", "percentage", "fixed", "full"]);

function safeNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function roundMoney(value) {
  return Math.round((safeNumber(value, 0) + Number.EPSILON) * 100) / 100;
}

function servicePriceType(service = {}) {
  const raw = String(service.priceType || service.pricingType || "fixed").toLowerCase().trim();
  if (raw === "from" || raw === "desde") return "from";
  if (raw === "quote" || raw === "cotizacion" || raw === "cotización") return "quote";
  return "fixed";
}

function servicePriceCents(service = {}) {
  const value = safeNumber(service.price, 0);
  return Math.max(0, Math.round(value * 100));
}

export function getBookingPaymentPolicyEditorValue(service = {}) {
  const raw =
    service && typeof service.bookingPaymentPolicy === "object" && service.bookingPaymentPolicy
      ? service.bookingPaymentPolicy
      : {};

  let type = String(raw.type || "").toLowerCase().trim();

  if (!POLICY_TYPES.has(type)) {
    const legacyPercent = safeNumber(service.depositPercent, 0);
    const legacyAmount = safeNumber(
      service.depositAmount ?? service.requiredDepositAmount ?? service.paymentDepositAmount,
      0
    );

    if (legacyPercent > 0) type = "percentage";
    else if (legacyAmount > 0) type = "fixed";
    else type = "none";
  }

  const percentage = safeNumber(
    raw.percentage ?? raw.percent ?? service.depositPercent,
    type === "percentage" ? 50 : 0
  );

  const amount = safeNumber(
    raw.amount ??
      raw.fixedAmount ??
      service.depositAmount ??
      service.requiredDepositAmount ??
      service.paymentDepositAmount,
    0
  );

  return {
    version: BOOKING_PAYMENT_POLICY_VERSION,
    type,
    enabled: type !== "none",
    percentage: type === "percentage" ? percentage : null,
    amount: type === "fixed" ? amount : null,
  };
}

export function bookingPaymentPolicyEligibility(service = {}) {
  const priceType = servicePriceType(service);
  const priceCents = servicePriceCents(service);

  if (priceType !== "fixed") {
    return {
      eligible: false,
      reason: "fixed-price-required",
      priceType,
      priceCents,
    };
  }

  if (priceCents <= 0) {
    return {
      eligible: false,
      reason: "positive-price-required",
      priceType,
      priceCents,
    };
  }

  return {
    eligible: true,
    reason: null,
    priceType,
    priceCents,
  };
}

export function serializeBookingPaymentPolicy(service = {}) {
  const eligibility = bookingPaymentPolicyEligibility(service);
  const editor = getBookingPaymentPolicyEditorValue(service);

  const none = {
    version: BOOKING_PAYMENT_POLICY_VERSION,
    type: "none",
    enabled: false,
    percentage: null,
    amount: null,
  };

  if (!eligibility.eligible || editor.type === "none") return none;

  if (editor.type === "percentage") {
    const percentage = Math.min(100, Math.max(0, safeNumber(editor.percentage, 0)));
    if (percentage <= 0) return none;

    return {
      version: BOOKING_PAYMENT_POLICY_VERSION,
      type: "percentage",
      enabled: true,
      percentage: roundMoney(percentage),
      amount: null,
    };
  }

  if (editor.type === "fixed") {
    const requestedCents = Math.max(0, Math.round(safeNumber(editor.amount, 0) * 100));
    if (requestedCents <= 0) return none;

    const cappedCents = Math.min(requestedCents, eligibility.priceCents);

    return {
      version: BOOKING_PAYMENT_POLICY_VERSION,
      type: "fixed",
      enabled: true,
      percentage: null,
      amount: roundMoney(cappedCents / 100),
    };
  }

  if (editor.type === "full") {
    return {
      version: BOOKING_PAYMENT_POLICY_VERSION,
      type: "full",
      enabled: true,
      percentage: null,
      amount: null,
    };
  }

  return none;
}

export function bookingPaymentDueNowCents(service = {}) {
  const eligibility = bookingPaymentPolicyEligibility(service);
  if (!eligibility.eligible) return 0;

  const policy = serializeBookingPaymentPolicy(service);

  if (!policy.enabled || policy.type === "none") return 0;
  if (policy.type === "full") return eligibility.priceCents;

  if (policy.type === "fixed") {
    return Math.min(
      eligibility.priceCents,
      Math.max(0, Math.round(safeNumber(policy.amount, 0) * 100))
    );
  }

  if (policy.type === "percentage") {
    const pct = Math.min(100, Math.max(0, safeNumber(policy.percentage, 0)));
    return Math.min(
      eligibility.priceCents,
      Math.max(0, Math.round((eligibility.priceCents * pct) / 100))
    );
  }

  return 0;
}

export function bookingPaymentBalanceCents(service = {}) {
  const eligibility = bookingPaymentPolicyEligibility(service);
  if (!eligibility.eligible) return 0;

  return Math.max(
    0,
    eligibility.priceCents - bookingPaymentDueNowCents(service)
  );
}

export function formatBookingPaymentPolicySummary(service = {}) {
  const policy = serializeBookingPaymentPolicy(service);

  if (!policy.enabled || policy.type === "none") return "Sin pago para reservar";
  if (policy.type === "full") return "Pago completo para reservar";
  if (policy.type === "percentage") return `${policy.percentage}% para reservar`;
  if (policy.type === "fixed") return `$${roundMoney(policy.amount)} para reservar`;

  return "Sin pago para reservar";
}
